import { firstMessage, getBackendErrorData } from '@/shared/api/api-client/backend-error';
import { ApiError } from '@/shared/api/api-client/api-error';


export type TeamSignupField = 'email' | 'password' | 'first_name' | 'last_name' | 'job_title' | 'phone';

export type TeamSignupError = {
    fieldErrors: Partial<Record<TeamSignupField, string>>;
    message    : string;
    /** Адрес уже зарегистрирован: предлагаем войти и принять приглашение. */
    suggestLogin: boolean;
};


const FIELDS: TeamSignupField[] = [ 'email', 'password', 'first_name', 'last_name', 'job_title', 'phone' ];

const ALREADY_REGISTERED = 'This email is already registered';
const INVALID_LINK       = 'This invitation link is no longer active. Please ask the team owner for a new one';
const EXPIRED            = 'This invitation has expired. Please ask the team owner for a new one';
const MISMATCH           = 'This invitation was sent to a different email address';
const BILLING_FAILED     = 'We could not finish creating your account. Please try again later';
const UNKNOWN            = 'Something went wrong. Please try again';


const plain = (message: string, extra: Partial<TeamSignupError> = {}): TeamSignupError => (
    { message, fieldErrors: {}, suggestLogin: false, ...extra }
);


/** Превращает ошибку регистрации по приглашению в сообщение и ошибки полей формы. */
export const mapTeamSignupError = (err: unknown): TeamSignupError => {
    if (!(err instanceof ApiError)) return plain(UNKNOWN);

    const data   = getBackendErrorData(err);
    const detail = firstMessage(data.detail);

    if (err.status === 404) return plain(INVALID_LINK);
    if (err.status === 410) return plain(EXPIRED);
    if (err.status === 422) return plain(MISMATCH, { fieldErrors: { email: MISMATCH } });
    if (err.status === 502 || detail === 'subscription_creation_failed') return plain(BILLING_FAILED);

    if (err.status === 400) {
        if (data.invite_token) return plain(INVALID_LINK);

        const fieldErrors: TeamSignupError['fieldErrors'] = {};

        for (const field of FIELDS) {
            const message = firstMessage(data[field]);

            if (message) fieldErrors[field] = message;
        }

        const emailTaken = /already exists/i.test(fieldErrors.email ?? '');

        if (emailTaken) {
            delete fieldErrors.email;

            return { message: ALREADY_REGISTERED, fieldErrors, suggestLogin: true };
        }

        if (Object.keys(fieldErrors).length) return plain('Please check the highlighted fields', { fieldErrors });
    }

    return plain(UNKNOWN);
};
